/**
 * Pooler for admin (VIB-3050 PR 3b): hvor et lead er nå, i klartekst. Tilstanden kommer fra
 * crm_lead_pool_state() i databasen, og reglene fra lead_rules(), så teksten følger det som skjer.
 */
import { formatDateShort, formatDateTimeShort } from "@/lib/format";
import type { LeadRules } from "@/lib/lead-queue";

export type PoolState = "free" | "queued" | "resting" | "meeting" | "customer" | "rejected";

export const POOL_STATE_LABELS: Record<PoolState, string> = {
  free: "Ledig",
  queued: "I køer",
  resting: "Hviler",
  meeting: "Møter",
  customer: "Kunde",
  rejected: "Avvist",
};

export interface PoolLeadStatus {
  state: PoolState;
  holder_name: string | null;
  last_outcome: string | null;
  next_eligible_at: string | null;
  parked_until: string | null;
  attempts_count: number;
  revisit_on?: string | null;
}

const firstName = (name: string) => name.split(" ")[0];
const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });
const isToday = (iso: string, now: Date) =>
  new Date(iso).toLocaleDateString("nb-NO", { timeZone: "Europe/Oslo" }) === now.toLocaleDateString("nb-NO", { timeZone: "Europe/Oslo" });

/** Kort status i tabellen: «Ledig», «Hos Ole · sperret til 12.15», «Hviler til 04.11». */
export function poolStatusText(s: PoolLeadStatus, now: Date = new Date()): string {
  switch (s.state) {
    case "queued": {
      const who = s.holder_name ? `Hos ${firstName(s.holder_name)}` : "I en kø";
      if (s.next_eligible_at && new Date(s.next_eligible_at) > now) {
        return `${who} · sperret til ${isToday(s.next_eligible_at, now) ? clock(s.next_eligible_at) : formatDateTimeShort(s.next_eligible_at)}`;
      }
      if (s.last_outcome === "call_back" && s.parked_until) return `${who} · ring tilbake ${formatDateShort(s.parked_until)}`;
      if (s.last_outcome === "email_planned" && s.parked_until) return `${who} · skal sende mail ${formatDateShort(s.parked_until)}`;
      return who;
    }
    case "resting": {
      const until = s.next_eligible_at ?? s.parked_until;
      return until ? `Hviler til ${formatDateShort(until)}` : "Hviler";
    }
    case "rejected":
      return s.revisit_on ? `Avvist · vurderes ${formatDateShort(s.revisit_on)}` : "Avvist";
    case "meeting":
      return "Møte booket";
    case "customer":
      return "Kunde";
    default:
      return "Ledig";
  }
}

/** «Hvorfor her» på leadkortet for admin: status med regelnummer og verdiene fra reglene. */
export function adminWhyHere(s: PoolLeadStatus & { second_try?: boolean; pool_name: string }, r: LeadRules, now: Date = new Date()): string {
  const status = poolStatusText(s, now);
  if (s.state === "queued" && s.last_outcome === "no_answer" && s.next_eligible_at && new Date(s.next_eligible_at) > now) {
    return `${status}. Fikk ikke svar, og andre forsøk kan tidligst skje ${r.retry_hours} timer senere (regel 3). Får bookeren ikke svar igjen, går leadet tilbake til ${s.pool_name} og hviler i ${r.pool_rest_days} dager (regel 4).`;
  }
  if (s.state === "queued" && (s.last_outcome === "call_back" || s.last_outcome === "email_sent" || s.last_outcome === "email_planned")) {
    return `${status}. Leadet er bookerens til møte eller avvisning (regel 5). Er bookeren borte, bruk «Tildel til …» eller «Tilbake til poolen».`;
  }
  if (s.state === "queued") {
    return `${status}. Leadet ligger i køen til bookeren og kan ringes nå (regel 1 og 2).`;
  }
  if (s.state === "resting" && s.attempts_count >= r.max_attempts) {
    return `${status}. Etter ${r.max_attempts} forsøk uten svar hviler leadet i ${r.park_days} dager (regel 7).`;
  }
  if (s.state === "resting" && s.last_outcome === "wrong_number") {
    return `${status}. Feil nummer: settes til side i ${r.wrong_number_days} dager mens noen finner et nytt nummer (regel 6).`;
  }
  if (s.state === "resting") {
    return `${status}. To «Ikke svar» på rad: leadet hviler i ${r.pool_rest_days} dager i ${s.pool_name} før en annen booker kan få det (regel 4).`;
  }
  if (s.state === "rejected") return `${status}. Ligger i Avviste og vurderes på nytt på datoen (regel 8).`;
  if (s.state === "free") return `Ledig i ${s.pool_name}. Fylles inn i køen til en booker som ringer fra poolen (regel 1).`;
  return `${status}.`;
}
